/**
 * Peon name normalization and matching. Every name that already appears
 * anywhere in the household data (server/db.ts's listPeonNames) is a
 * registered peon; entering one just switches the device to it, entering
 * anything else is a rename. Shared by the server (functions/api/peons/*.ts)
 * and the client (src/components/AccountSheet.tsx).
 */

/**
 * Names the server writes for itself, never a peon: "mail" on the Device log rows the e-transfer
 * mail handler leaves (live-worker/src/mail.ts) and "Interac email" as who settled a request the
 * deposit email confirmed (shared/wood.ts WOOD_EMAIL_SETTLER). Kept out of the peon list and
 * refused as a peon's name.
 */
/** The longest a peon's name may be, in characters, on every gate and in the rename route. */
export const NAME_MAX = 60;

export const SYSTEM_NAMES: readonly string[] = ["mail", "Interac email"];

/** Whether `name` is one of SYSTEM_NAMES (normalized, ignoring case). */
export function isSystemName(name: string): boolean {
  const n = normalizeName(name).toLowerCase();
  return SYSTEM_NAMES.some((s) => s.toLowerCase() === n);
}

/** Trims and collapses internal whitespace runs to a single space. */
export function normalizeName(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

export type NameChange =
  | { action: "same" }
  | { action: "switch"; name: string }
  | { action: "rename"; name: string };

/**
 * Decides what entering `requested` as this device's name should do.
 * Matching is case-insensitive on normalized names.
 * - Same as `current` (after normalizing, ignoring case): "same", nothing to do.
 * - Matches a name in `known`: "switch", to the registered spelling from `known`.
 * - Otherwise: "rename", to the normalized spelling of `requested`.
 */
export function resolveNameChange(current: string, requested: string, known: readonly string[]): NameChange {
  const normalized = normalizeName(requested);
  if (normalized.toLowerCase() === normalizeName(current).toLowerCase()) return { action: "same" };
  const match = known.find((k) => normalizeName(k).toLowerCase() === normalized.toLowerCase());
  if (match) return { action: "switch", name: normalizeName(match) };
  return { action: "rename", name: normalized };
}
